// ============================================================
// 天水數位店長 — 打卡頁面
// Kiosk 模式（iPad）+ 個人模式（手機）
// ============================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAttendanceStore } from '../stores/attendanceStore';
import { useAuthStore } from '../stores/authStore';
import { api } from '../lib/api';
import { getBestPosition } from '../lib/geolocation';
import { QrScannerModal } from './QrScannerModal';
import { LateReasonModal } from './LateReasonModal';

type ClockMethod = 'QR' | 'WIFI' | 'GPS';
type ViewMode = 'kiosk' | 'personal';

// ─── Kiosk View ─────────────────────────────────────────────

function KioskView() {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [timeDisplay, setTimeDisplay] = useState('');
  const { todayRecords, fetchToday } = useAttendanceStore();

  useEffect(() => {
    fetchToday();
  }, [fetchToday]);

  // 時鐘
  useEffect(() => {
    const tick = () => setTimeDisplay(
      new Date().toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    );
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  // QR Code 輪詢
  useEffect(() => {
    let active = true;
    const fetchQR = async () => {
      try {
        const data = await api.get<{ qrDataUrl: string }>('/qr/current');
        if (active) setQrDataUrl(data.qrDataUrl);
      } catch {
        // 靜默失敗
      }
    };
    fetchQR();
    const interval = setInterval(fetchQR, 15_000);
    return () => { active = false; clearInterval(interval); };
  }, []);

  const statusColor: Record<string, string> = {
    NORMAL: 'text-sage-500', LATE: 'text-rose-500',
    EARLY_LEAVE: 'text-rose-400', PENDING: 'text-smoke-400',
  };
  const statusLabel: Record<string, string> = {
    NORMAL: '正常', LATE: '遲到', EARLY_LEAVE: '早退', PENDING: '待確認',
  };

  return (
    <div className="min-h-page flex flex-col items-center justify-center bg-morandi-50 p-8 pb-nav">
      <div className="text-6xl font-mono font-light text-morandi-800 mb-2 tracking-widest">{timeDisplay}</div>
      <div className="text-lg text-morandi-400 mb-8">
        {new Date().toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
      </div>

      <div className="bg-white rounded-card shadow-card-lg p-8 mb-8">
        {qrDataUrl ? (
          <img src={qrDataUrl} alt="打卡 QR Code" className="w-64 h-64" />
        ) : (
          <div className="w-64 h-64 bg-morandi-100 rounded-card flex items-center justify-center">
            <div className="text-center">
              <div className="text-4xl text-morandi-300 mb-2">QR</div>
              <div className="text-sm text-morandi-400">掃描打卡</div>
              <div className="text-xs text-morandi-300 mt-1 animate-pulse-soft">每 15 秒自動更新</div>
            </div>
          </div>
        )}
      </div>

      <div className="w-full max-w-md">
        <h2 className="text-sm text-morandi-400 mb-3 text-center">今日出勤</h2>
        <div className="space-y-2">
          {todayRecords.length === 0 ? (
            <div className="text-center text-morandi-300 text-sm py-4">尚無打卡紀錄</div>
          ) : (
            todayRecords.map((r) => (
              <div key={r.id} className="flex items-center justify-between bg-white rounded-cell px-4 py-3 shadow-card">
                <span className="text-sm font-medium text-morandi-700">{r.user?.name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-morandi-400">
                    {r.clockIn ? new Date(r.clockIn).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }) : ''}
                    {r.clockOut ? ` — ${new Date(r.clockOut).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}` : ''}
                  </span>
                  <span className={`text-xs font-medium ${statusColor[r.status] ?? 'text-morandi-400'}`}>
                    {statusLabel[r.status] ?? r.status}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Personal View ──────────────────────────────────────────

function PersonalView() {
  // 本店實際啟用的打卡方式（由後端提供）。先前是寫死顯示三種並預設 WiFi，
  // 若該方式未啟用，員工選了只會得到「此店鋪未啟用…驗證」。
  const [enabledMethods, setEnabledMethods] = useState<ClockMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<ClockMethod>('QR');

  // 取得本店啟用的打卡方式，並把預設選項設為「最方便且可用」的那一個
  useEffect(() => {
    api.get<{ clockMethods?: string[] }>('/attendance/today')
      .then(r => {
        const list = (r.clockMethods ?? []) as ClockMethod[];
        if (!list.length) return;
        setEnabledMethods(list);
        // 優先順序：GPS（一鍵且可驗證）→ WIFI（一鍵）→ QR（需掃描）
        const preferred = (['GPS', 'WIFI', 'QR'] as ClockMethod[]).find(m => list.includes(m));
        if (preferred) setSelectedMethod(preferred);
      })
      .catch(() => { /* 取不到就維持預設，後端仍會擋掉未啟用的方式 */ });
  }, []);

  const { isClocking, clockResult, clockIn, clockOut, breakOut, breakIn, clearClockResult, todayRecords, fetchToday } = useAttendanceStore();
  const { user } = useAuthStore();
  const [timeDisplay, setTimeDisplay] = useState('');
  const [showQrScanner, setShowQrScanner] = useState(false);
  // 掃 QR 是為了哪個動作：一般打卡，或「跳過休息直接下班」
  const [qrIntent, setQrIntent] = useState<'normal' | 'skipBreak'>('normal');
  // 定位最多等 10 秒，期間按鈕要顯示狀態、不能重按
  const [locating, setLocating] = useState(false);
  const [lateInfo, setLateInfo] = useState<{ scheduledStart: string; lateMinutes: number; payload: Record<string, unknown> } | null>(null);
  const [todayScheduleStart, setTodayScheduleStart] = useState<string | null>(null);
  // 下班二次確認 dialog（修 bug 5/8 防誤觸把休息結束當下班）
  const [confirmClockOut, setConfirmClockOut] = useState<{ payload: Record<string, unknown>; workedHours: number } | null>(null);

  // 判斷今日打卡狀態（4 段）
  const myRecord = todayRecords.find((r) => r.userId === user?.id);
  const myOpenRecord = todayRecords.find((r) => r.userId === user?.id && !r.clockOut);
  const hasClockedIn = !!myOpenRecord;

  // 休息開始最小冷卻：避免員工誤觸馬上按到「上班」結束休息（修 bug 5/8）
  const BREAK_MIN_COOLDOWN_MIN = 30;
  const canBreakInNow = useMemo(() => {
    if (!myRecord?.breakOut) return true;
    const elapsed = (Date.now() - new Date(myRecord.breakOut).getTime()) / 60000;
    return elapsed >= BREAK_MIN_COOLDOWN_MIN;
  }, [myRecord?.breakOut]);
  const breakCooldownRemaining = useMemo(() => {
    if (!myRecord?.breakOut) return 0;
    const elapsed = (Date.now() - new Date(myRecord.breakOut).getTime()) / 60000;
    return Math.max(0, Math.ceil(BREAK_MIN_COOLDOWN_MIN - elapsed));
  }, [myRecord?.breakOut, timeDisplay]);  // timeDisplay 每秒更新觸發 re-render
  // 狀態機：NOT_STARTED | WORKING | ON_BREAK | RETURNED | FINISHED
  const clockState: 'NOT_STARTED' | 'WORKING' | 'ON_BREAK' | 'RETURNED' | 'FINISHED' = (() => {
    if (!myRecord) return 'NOT_STARTED';
    if (myRecord.clockOut) return 'FINISHED';
    if (myRecord.breakIn) return 'RETURNED';
    if (myRecord.breakOut) return 'ON_BREAK';
    return 'WORKING';
  })();
  // 是否該有休息（依當天排班的 breakStart/End 判斷）
  const hasScheduledBreak = !!(myRecord?.schedule?.breakStart && myRecord?.schedule?.breakEnd);

  useEffect(() => {
    fetchToday();
  }, [fetchToday]);

  // 取得今日排班的開始時間（用來判斷是否遲到）
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 修 bug 5/11：用 Taipei 日期，不是 toISOString() (UTC)
        // Taipei 凌晨 0-8 點打卡，UTC 還在前一天，會抓到前一天的排班（例假日 startTime=00:00）
        // 算出「遲到 479 分鐘」嚇員工
        const taipeiDateStr = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' });
        const today = new Date();
        const offset = today.getDay() === 0 ? -6 : 1 - today.getDay();
        const ws = new Date(today);
        ws.setDate(ws.getDate() + offset);
        const wsStr = taipeiDateStr(ws);
        const todayStr = taipeiDateStr(today);
        const data = await api.get<{ schedules: Array<{ userId: string; date: string; startTime: string; template?: { kind?: string } | null }> }>(
          `/schedules?weekStart=${wsStr}`
        );
        if (cancelled) return;
        // 過濾掉 REGULAR_LEAVE / REST_DAY（startTime=00:00 不該被當成「上班時間」算遲到）
        const mine = data.schedules.find(s => {
          if (s.userId !== user?.id) return false;
          if (!s.date.startsWith(todayStr)) return false;
          const kind = s.template?.kind ?? 'WORK';
          return kind === 'WORK';
        });
        setTodayScheduleStart(mine?.startTime ?? null);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    const tick = () => setTimeDisplay(
      new Date().toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })
    );
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  // 3 秒後清除結果
  useEffect(() => {
    if (clockResult) {
      const t = setTimeout(clearClockResult, 3000);
      return () => clearTimeout(t);
    }
  }, [clockResult, clearClockResult]);

  // 計算是否遲到（容許 5 分鐘）
  const computeLateMinutes = useCallback((): number => {
    if (!todayScheduleStart || hasClockedIn) return 0;
    const [sh, sm] = todayScheduleStart.split(':').map(Number);
    const now = new Date();
    const start = new Date(now);
    start.setHours(sh, sm, 0, 0);
    const diffMin = Math.floor((now.getTime() - start.getTime()) / 60000);
    return diffMin > 5 ? diffMin : 0; // 寬限 5 分
  }, [todayScheduleStart, hasClockedIn]);

  // 計算今日已工作時數（給下班確認 dialog 顯示）
  const calcTodayWorkedHours = useCallback((): number => {
    if (!myRecord?.clockIn) return 0;
    const start = new Date(myRecord.clockIn).getTime();
    const now = Date.now();
    let total = (now - start) / 3600000;
    // 扣除已記錄的休息時間
    if (myRecord.breakOut && myRecord.breakIn) {
      const bStart = new Date(myRecord.breakOut).getTime();
      const bEnd = new Date(myRecord.breakIn).getTime();
      total -= (bEnd - bStart) / 3600000;
    } else if (myRecord.breakOut && !myRecord.breakIn) {
      // 只有 breakOut 沒 breakIn，扣到現在
      const bStart = new Date(myRecord.breakOut).getTime();
      total -= (now - bStart) / 3600000;
    }
    return Math.max(0, Math.round(total * 10) / 10);
  }, [myRecord]);

  // 真正執行打卡（依目前狀態機決定打哪個；含防誤觸保護）
  const submitClock = useCallback(async (payload: Record<string, unknown>) => {
    if (clockState === 'NOT_STARTED') {
      await clockIn(payload as any);
      return;
    }
    if (clockState === 'WORKING' && hasScheduledBreak) {
      await breakOut(payload as any);
      return;
    }
    if (clockState === 'ON_BREAK') {
      // 防誤觸：休息開始 30 分鐘內不可結束休息（修 bug 5/8 劉伊純 49 秒誤觸）
      if (!canBreakInNow) {
        alert(`休息至少 ${BREAK_MIN_COOLDOWN_MIN} 分鐘，還剩 ${breakCooldownRemaining} 分鐘才能結束休息。如有特殊狀況請聯絡主管。`);
        return;
      }
      await breakIn(payload as any);
      return;
    }
    // WORKING (no break) | RETURNED → 下班，加二次確認
    setConfirmClockOut({ payload, workedHours: calcTodayWorkedHours() });
  }, [clockState, hasScheduledBreak, clockIn, breakOut, breakIn, canBreakInNow, breakCooldownRemaining, calcTodayWorkedHours]);

  // 確認後實際送下班
  const handleConfirmClockOut = useCallback(async () => {
    if (!confirmClockOut) return;
    const { payload } = confirmClockOut;
    setConfirmClockOut(null);
    await clockOut(payload as any);
  }, [confirmClockOut, clockOut]);

  // 依選擇的方式準備打卡資料。
  // GPS → 取定位（含誤差）；WIFI → 直接回；QR → 開相機，掃完由 handleQrScanned 接手（回 null）。
  // 「跳過休息直接下班」也走這裡——以前那條路只送 { method }，選定位時一定被後端擋下。
  const buildPayload = useCallback(async (intent: 'normal' | 'skipBreak'): Promise<Record<string, unknown> | null> => {
    const payload: Record<string, unknown> = { method: selectedMethod };

    if (selectedMethod === 'WIFI') {
      // 不再把店鋪 WiFi 名稱寫死在前端（那等於公開該設定，且瀏覽器本來
      // 就讀不到真實 SSID，送回去只是自問自答）。後端已改為不要求此欄位。
      return payload;
    }
    if (selectedMethod === 'GPS') {
      setLocating(true);
      try {
        const fix = await getBestPosition();
        return { ...payload, ...fix };
      } catch (e) {
        alert((e as Error).message);
        return null;
      } finally {
        setLocating(false);
      }
    }
    // QR 模式：開啟相機掃描
    setQrIntent(intent);
    setShowQrScanner(true);
    return null;
  }, [selectedMethod]);

  const prepareAndClock = useCallback(async () => {
    const payload = await buildPayload('normal');
    if (!payload) return;

    // 上班打卡 + 已遲到 → 跳遲到原因 Modal
    const lateMin = computeLateMinutes();
    if (!hasClockedIn && lateMin > 0 && todayScheduleStart) {
      setLateInfo({ scheduledStart: todayScheduleStart, lateMinutes: lateMin, payload });
      return;
    }

    await submitClock(payload);
  }, [buildPayload, computeLateMinutes, hasClockedIn, todayScheduleStart, submitClock]);

  const handleClock = prepareAndClock;

  // QR 掃描完成
  const handleQrScanned = useCallback(async (raw: string) => {
    setShowQrScanner(false);
    // 店內 QR 的內容是 JSON（{ type, token, ... }），要取出 token 再送。
    // 以前整串原樣送出，後端比對不到 → 這個分頁的 QR 打卡從來沒成功過。
    let qrToken = raw;
    try {
      const parsed = JSON.parse(raw);
      qrToken = parsed.token ?? raw;
    } catch { /* 非 JSON 直接當 token */ }
    const payload: Record<string, unknown> = { method: 'QR', qrToken };

    if (qrIntent === 'skipBreak') {
      setQrIntent('normal');
      setConfirmClockOut({ payload, workedHours: calcTodayWorkedHours() });
      return;
    }

    const lateMin = computeLateMinutes();
    if (!hasClockedIn && lateMin > 0 && todayScheduleStart) {
      setLateInfo({ scheduledStart: todayScheduleStart, lateMinutes: lateMin, payload });
      return;
    }
    await submitClock(payload);
  }, [qrIntent, calcTodayWorkedHours, computeLateMinutes, hasClockedIn, todayScheduleStart, submitClock]);

  // 遲到原因確認後送出
  const handleLateConfirm = useCallback(async (note: string) => {
    if (!lateInfo) return;
    const payload = { ...lateInfo.payload, note };
    setLateInfo(null);
    await submitClock(payload);
  }, [lateInfo, submitClock]);

  const ALL_METHODS: { key: ClockMethod; label: string; icon: string; desc: string }[] = [
    { key: 'GPS', label: '定位', icon: '◎', desc: '一鍵，需開啟定位' },
    { key: 'WIFI', label: 'WiFi', icon: '◉', desc: '一鍵' },
    { key: 'QR', label: 'QR Code', icon: '⊞', desc: '掃描店內 QR' },
  ];
  // 只顯示本店啟用的方式；尚未取得設定前先全部顯示，避免畫面空白
  const methods = enabledMethods.length
    ? ALL_METHODS.filter(m => enabledMethods.includes(m.key))
    : ALL_METHODS;

  return (
    <div className="min-h-page flex flex-col items-center justify-center bg-morandi-50 p-6 pb-nav">
      <div className="text-5xl font-mono font-light text-morandi-800 mb-1">{timeDisplay}</div>
      <div className="text-sm text-morandi-400 mb-10">
        {new Date().toLocaleDateString('zh-TW', { month: 'long', day: 'numeric', weekday: 'long' })}
      </div>

      <div className="flex gap-3 mb-8">
        {methods.map((m) => (
          <button
            key={m.key}
            onClick={() => setSelectedMethod(m.key)}
            className={`flex flex-col items-center px-5 py-4 rounded-card transition-all min-w-[100px]
              ${selectedMethod === m.key ? 'bg-white shadow-card-lg ring-2 ring-smoke-300' : 'bg-morandi-100 hover:bg-white hover:shadow-card'}`}
          >
            <span className="text-2xl mb-1">{m.icon}</span>
            <span className="text-sm font-medium text-morandi-700">{m.label}</span>
            <span className="text-xs text-morandi-400 mt-0.5">{m.desc}</span>
          </button>
        ))}
      </div>

      {(() => {
        let mainLabel = '上班';
        let mainSubLabel = '打卡';
        let mainColor = 'bg-sage-500 hover:bg-sage-600';
        let secondary: { label: string; color: string; action: () => void } | null = null;

        if (clockState === 'NOT_STARTED') {
          mainLabel = '上班';
          mainColor = 'bg-sage-500 hover:bg-sage-600';
        } else if (clockState === 'WORKING' && hasScheduledBreak) {
          mainLabel = '休息';
          mainColor = 'bg-amber-500 hover:bg-amber-600';
          // 副按鈕：跳過休息直接下班（同樣會經過二次確認 dialog）
          secondary = {
            label: '⚠ 跳過休息直接下班',
            color: 'bg-rose-400 hover:bg-rose-500',
            action: async () => {
              const payload = await buildPayload('skipBreak');
              if (payload) setConfirmClockOut({ payload, workedHours: calcTodayWorkedHours() });
            },
          };
        } else if (clockState === 'ON_BREAK') {
          // 員工心智模型：休息結束 = 再次上班
          // 視覺差異：休息中按鈕仍是「上班」(綠)，但加上冷卻倒數提示
          if (canBreakInNow) {
            mainLabel = '上班';
            mainSubLabel = '結束休息';
            mainColor = 'bg-sage-500 hover:bg-sage-600';
          } else {
            mainLabel = `休息中`;
            mainSubLabel = `還剩 ${breakCooldownRemaining} 分鐘`;
            mainColor = 'bg-morandi-300 cursor-not-allowed';
          }
        } else if (clockState === 'RETURNED') {
          // 修 bug 5/8：RETURNED 狀態下班鈕視覺強化（避免員工以為是「結束休息」）
          mainLabel = '下班';
          mainSubLabel = '⚠ 結束今日';
          mainColor = 'bg-rose-500 hover:bg-rose-600';
        } else if (clockState === 'WORKING') {
          mainLabel = '下班';
          mainColor = 'bg-rose-400 hover:bg-rose-500';
        } else if (clockState === 'FINISHED') {
          mainLabel = '已下班';
          mainSubLabel = '今日已結束';
          mainColor = 'bg-morandi-300 cursor-not-allowed';
        }

        const buttonDisabled = isClocking
          || locating
          || clockState === 'FINISHED'
          || (clockState === 'ON_BREAK' && !canBreakInNow);

        return (
          <>
            <button
              onClick={handleClock}
              disabled={buttonDisabled}
              className={`w-48 h-48 rounded-full font-bold text-xl text-white transition-all active:scale-95 shadow-float
                ${isClocking || locating ? 'bg-morandi-300 cursor-wait' : mainColor}`}
            >
              {locating ? (
                <span className="animate-pulse-soft">定位中...</span>
              ) : isClocking ? (
                <span className="animate-pulse-soft">驗證中...</span>
              ) : (
                <div>
                  <div className="text-2xl">{mainLabel}</div>
                  <div className="text-sm font-normal opacity-80 mt-1">{mainSubLabel}</div>
                </div>
              )}
            </button>

            {/* 進度標示 */}
            {clockState !== 'NOT_STARTED' && clockState !== 'FINISHED' && (
              <div className="mt-4 text-xs text-morandi-500 flex items-center gap-2">
                <span>✓ 上班 {myRecord?.clockIn ? new Date(myRecord.clockIn).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                {myRecord?.breakOut && <span>· ✓ 休息 {new Date(myRecord.breakOut).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}</span>}
                {myRecord?.breakIn && <span>· ✓ 返回 {new Date(myRecord.breakIn).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}</span>}
              </div>
            )}

            {secondary && (
              <button
                onClick={secondary.action}
                disabled={isClocking || locating}
                className={`mt-3 px-4 py-2 rounded-btn text-sm text-white transition-colors ${secondary.color}`}
              >
                {secondary.label}
              </button>
            )}
          </>
        );
      })()}

      {clockResult && (
        <div className={`mt-6 px-6 py-3 rounded-card text-sm font-medium animate-slide-up
          ${clockResult.success ? 'bg-sage-100 text-sage-600 border border-sage-200' : 'bg-rose-100 text-rose-600 border border-rose-200'}`}>
          {clockResult.message}
        </div>
      )}

      {showQrScanner && (
        <QrScannerModal
          onScan={handleQrScanned}
          onClose={() => { setShowQrScanner(false); setQrIntent('normal'); }}
        />
      )}

      {lateInfo && (
        <LateReasonModal
          scheduledStart={lateInfo.scheduledStart}
          lateMinutes={lateInfo.lateMinutes}
          onConfirm={handleLateConfirm}
          onCancel={() => setLateInfo(null)}
        />
      )}

      {/* 下班二次確認 dialog（修 bug 5/8 防誤觸） */}
      {confirmClockOut && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setConfirmClockOut(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="text-5xl mb-2">⚠</div>
              <h2 className="text-xl font-bold text-morandi-800">確定要下班嗎？</h2>
              <p className="text-sm text-morandi-500 mt-2">
                今天工作時間：<span className="font-mono font-bold text-morandi-700">{confirmClockOut.workedHours}</span> 小時
              </p>
              {confirmClockOut.workedHours < 6 && (
                <p className="text-xs text-amber-600 mt-2 bg-amber-50 rounded p-2">
                  工時較短，是否誤觸？若想結束休息請按「取消」回上一頁。
                </p>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setConfirmClockOut(null)}
                className="flex-1 py-3 bg-morandi-100 hover:bg-morandi-200 text-morandi-700 rounded-btn font-medium"
              >
                取消
              </button>
              <button
                onClick={handleConfirmClockOut}
                className="flex-1 py-3 bg-rose-500 hover:bg-rose-600 text-white rounded-btn font-medium"
              >
                確定下班
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────

export function ClockInPage() {
  const { user } = useAuthStore();
  const [mode, setMode] = useState<ViewMode>(
    user?.role === 'STAFF' ? 'personal' : 'kiosk'
  );

  return (
    <div className="relative">
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={() => setMode((m) => (m === 'kiosk' ? 'personal' : 'kiosk'))}
          className="px-3 py-1.5 bg-morandi-200 text-morandi-600 rounded-chip text-xs hover:bg-morandi-300 transition-colors"
        >
          {mode === 'kiosk' ? '切換至個人模式' : '切換至 Kiosk 模式'}
        </button>
      </div>
      {mode === 'kiosk' ? <KioskView /> : <PersonalView />}
    </div>
  );
}
